import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { SearchSelect } from "../../../shared/components/SearchSelect";
import { getReservaciones, revisarReservacion, cambiarFechaReservacion, deleteReservacion } from "../services/portalService";
import NuevaReservacionModal from "../components/NuevaReservacionModal";
import ParametrosReservacionModal from "../components/ParametrosReservacionModal";
import DatosPagoFields, { pagoInicial, validarPago, pagoPayload, SIN_COSTO } from "../components/DatosPagoFields";
import {
  ESTILOS_LISTA, StatCard, PestanasEstado, Desplegable, RangoFechas, Buscador,
  SelectorVista, BarraResultados, Pagination, Vacio, Cargando, EstadoBadge as Badge, ModalOverlay,
  RevisadoPor, fechaLocal, fechaLarga, fechaHoraLarga, diasEntre, hace, desdeHace,
  labelSt, fldStyle, metaSt, colLabelSt,
} from "../components/adminListaUI";
import styles from "../../finance/invoices/styles/invoice.module.css";

const PAGE_SIZE = 10;

const OPCIONES_ORDEN = [
  ["recientes", "Más recientes"],
  ["antiguas",  "Más antiguas"],
  ["proximas",  "Reserva más próxima"],
  ["lejanas",   "Reserva más lejana"],
];

const ESTADOS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b",  icon: "fa-clock" },
  APROBADA:  { bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.3)",  text: "#10b981",  icon: "fa-circle-check" },
  RECHAZADA: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171",  icon: "fa-circle-xmark" },
  CANCELADA: { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af",  icon: "fa-ban" },
};

/** Ícono según el tipo de área, para que la lista se lea de un vistazo. */
function iconoArea(nombre) {
  const n = (nombre || "").toLowerCase();
  if (/parrilla|bbq|asad/.test(n))          return "fa-fire-burner";
  if (/rancho|quincho/.test(n))             return "fa-tree";
  if (/piscina|pool/.test(n))               return "fa-person-swimming";
  if (/gimnas|gym/.test(n))                 return "fa-dumbbell";
  if (/cancha|futbol|fútbol|tenis/.test(n)) return "fa-futbol";
  if (/sal[oó]n|eventos|fiesta/.test(n))    return "fa-champagne-glasses";
  if (/juegos|infantil|parque/.test(n))     return "fa-child-reaching";
  return "fa-house";
}

/** Ícono del horario: primero por nombre, si no por la hora de inicio. */
function iconoHorario(nombre, inicio) {
  const n = (nombre || "").toLowerCase();
  if (/noche|nocturn/.test(n))    return "fa-moon";
  if (/ma[ñn]ana/.test(n))        return "fa-sun";
  if (/tarde/.test(n))            return "fa-cloud-sun";
  if (/completo|todo el d/.test(n)) return "fa-sun";
  const h = parseInt(String(inicio || "").slice(0, 2), 10);
  if (!isNaN(h)) {
    if (h < 12) return "fa-sun";
    if (h < 18) return "fa-cloud-sun";
    return "fa-moon";
  }
  return "fa-clock";
}

/**
 * Pastilla del estado de la reservación. `auto` marca la que canceló el
 * sistema: el estado es el mismo que cuando la cancela el residente, así que
 * sin esto no se distinguirían en la lista.
 */
function EstadoBadge({ estado, auto }) {
  const cfg = ESTADOS[estado] || ESTADOS.PENDIENTE;
  const sola = auto && estado === "CANCELADA";
  return <Badge cfg={{ ...cfg, icon: sola ? "fa-robot" : cfg.icon, label: sola ? "CANCELADA SOLA" : estado }} />;
}

/** Participio con el que arranca la línea de quién resolvió la solicitud. */
const VERBO_REVISION = { APROBADA: "Aprobada", RECHAZADA: "Rechazada", CANCELADA: "Cancelada" };

/**
 * Quién dejó la reserva como está.
 *
 * No siempre es una persona. El sistema también la mueve solo: cancela la que
 * siguió pendiente después de la fecha reservada (eso sí deja marca propia,
 * `cancelada_auto`) y rechaza la que venció sin que se confirmara el pago, que
 * no deja marca pero tampoco deja usuario. Como una reserva que resuelve un
 * administrador guarda siempre `revisado_por`, una RECHAZADA con fecha de
 * revisión y sin usuario es la automática.
 *
 * Y una CANCELADA que no es automática la canceló el residente: es el único
 * otro camino a ese estado, y ahí no se guarda usuario porque no hace falta.
 */
function revisionDe(r) {
  if (r.cancelada_auto) return { nombre: null, fecha: r.revisado_at, automatico: true };
  if (r.estado === "RECHAZADA" && r.revisado_at && !r.revisado_por_nombre) {
    return { nombre: null, fecha: r.revisado_at, automatico: true };
  }
  if (r.estado === "CANCELADA" && !r.revisado_por_nombre) {
    return { nombre: "el residente", fecha: r.revisado_at, automatico: false };
  }
  return { nombre: r.revisado_por_nombre, fecha: r.revisado_at, automatico: false };
}

/**
 * La línea de "quién la resolvió", o nada si sigue pendiente.
 *
 * En la fila va con `flexBasis: 100%` para que se lleve un renglón entero: la
 * columna es angosta y, compartiéndolo con la filial y la fecha, la hora se
 * cortaba con puntos suspensivos justo cuando es lo que se quiere leer.
 */
function revisadoDe(r, renglonPropio = false) {
  const verbo = VERBO_REVISION[r.estado];
  if (!verbo) return null;
  const estilo = renglonPropio ? { ...metaSt, flexBasis: "100%" } : metaSt;
  return <RevisadoPor verbo={verbo} estilo={estilo} {...revisionDe(r)} />;
}

export default function AdminReservaciones() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [reservaciones, setReservaciones] = useState([]);
  const [filiales,      setFiliales]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filtro,        setFiltro]        = useState("PENDIENTE");
  const [modal,         setModal]         = useState(null);
  const [notas,         setNotas]         = useState("");
  const [saving,        setSaving]        = useState(false);
  const [pago,          setPago]          = useState(pagoInicial);
  const [revErr,        setRevErr]        = useState("");
  const [fechaModal,    setFechaModal]    = useState(null);
  const [nuevaFecha,    setNuevaFecha]    = useState("");
  const [savingFecha,   setSavingFecha]   = useState(false);
  const [filterFilial,  setFilterFilial]  = useState("");
  const [filterArea,    setFilterArea]    = useState("");
  const [filterDesde,   setFilterDesde]   = useState("");
  const [filterHasta,   setFilterHasta]   = useState("");
  const [busqueda,      setBusqueda]      = useState("");
  const [orden,         setOrden]         = useState("recientes");
  const [vista,         setVista]         = useState("lista");
  const [expandida,     setExpandida]     = useState(null);
  const [page,          setPage]          = useState(1);
  const [confirmDel,    setConfirmDel]    = useState(null); // reservación a eliminar
  const [deleting,      setDeleting]      = useState(false);
  const [nuevaOpen,     setNuevaOpen]     = useState(false);
  const [paramsOpen,    setParamsOpen]    = useState(false);

  async function handleEliminar() {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await deleteReservacion(confirmDel.id);
      showToast("Reservación eliminada", true);
      setConfirmDel(null);
      load();
    } catch (e) {
      showToast(e.message, false);
      setConfirmDel(null);
    } finally { setDeleting(false); }
  }

  function load() {
    getReservaciones().then(d => setReservaciones(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/").then(r => r.json()).then(d => setFiliales(d.results || d)).catch(() => {});
  }, []);

  // Si un residente hace una reserva nueva mientras estamos acá, se actualiza
  // la lista sola en vez de esperar a que alguien la recargue a mano.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "RESERVA") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  function abrirRevision(r) {
    setModal(r); setNotas(""); setRevErr("");
    // La fecha de pago arranca en hoy; la cuenta y el método se conservan de
    // la última revisión porque casi siempre es el mismo banco.
    setPago(p => ({ ...pagoInicial(), metodo_pago: p.metodo_pago, cuenta_bancaria: p.cuenta_bancaria }));
  }
  async function handleRechazar() {
    setSaving(true); setRevErr("");
    try {
      await revisarReservacion(modal.id, { estado: "RECHAZADA", notas_admin: notas });
      showToast(t("portal.reservaRechazada"), false);
      setModal(null); setNotas(""); load();
    } catch { setRevErr(t("common.errorGuardar")); }
    finally { setSaving(false); }
  }
  async function handleAprobar() {
    const e = validarPago(pago);
    if (e) { setRevErr(e); return; }
    setSaving(true); setRevErr("");
    try {
      await revisarReservacion(modal.id, { estado: "APROBADA", notas_admin: notas, ...pagoPayload(pago) });
      showToast(t("portal.reservaAprobada"), true);
      setModal(null); setNotas(""); load();
    } catch (err) {
      try { const d = JSON.parse(err.message); setRevErr(Object.values(d).flat().join(". ")); }
      catch { setRevErr(err.message || t("common.errorGuardar")); }
    } finally { setSaving(false); }
  }
  async function handleCambiarFecha() {
    if (!nuevaFecha) return;
    setSavingFecha(true);
    try {
      await cambiarFechaReservacion(fechaModal.id, nuevaFecha);
      showToast("Fecha actualizada", true);
      setFechaModal(null); load();
    } catch { showToast(t("common.errorGuardar"), false); }
    finally { setSavingFecha(false); }
  }

  // ── Conteos, filtrado y orden ───────────────────────────────────────────
  const conteos = useMemo(() => ({
    TODAS:     reservaciones.length,
    PENDIENTE: reservaciones.filter(r => r.estado === "PENDIENTE").length,
    APROBADA:  reservaciones.filter(r => r.estado === "APROBADA").length,
    RECHAZADA: reservaciones.filter(r => r.estado === "RECHAZADA").length,
    CANCELADA: reservaciones.filter(r => r.estado === "CANCELADA").length,
  }), [reservaciones]);

  // Opciones de los SearchSelect. La primera ("") es el "sin filtro": así el
  // campo muestra "Todas las …" en vez del placeholder cuando no hay filtro, y
  // se puede volver atrás sin un botón aparte.
  const opcionesFilial = useMemo(() => [
    { id: "", primary: "Todas las filiales" },
    ...filiales.map(f => ({
      id: String(f.id),
      primary: `Filial ${f.idFilial}`,
      secondary: f.propietario_nombre || "",
    })),
  ], [filiales]);

  const opcionesArea = useMemo(() => {
    const vistas = new Map();
    reservaciones.forEach(r => { if (r.area && !vistas.has(r.area)) vistas.set(r.area, r.area_nombre); });
    return [
      { id: "", primary: "Todas las áreas" },
      ...[...vistas]
        .map(([id, nombre]) => ({ id: String(id), primary: nombre }))
        .sort((a, b) => a.primary.localeCompare(b.primary)),
    ];
  }, [reservaciones]);

  const filtrosActivos = [filterFilial, filterArea].filter(Boolean).length;
  const fechasActivas  = [filterDesde, filterHasta].filter(Boolean).length;

  const filtered = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    const nombreFilial = filiales.find(f => String(f.id) === String(filterFilial))?.idFilial;
    const lista = reservaciones.filter(r => {
      if (filtro !== "TODAS" && r.estado !== filtro) return false;
      if (filterFilial && r.filial_nombre !== nombreFilial) return false;
      if (filterArea && String(r.area) !== String(filterArea)) return false;
      if (filterDesde && r.fecha < filterDesde) return false;
      if (filterHasta && r.fecha > filterHasta) return false;
      if (texto) {
        const heno = `${r.area_nombre || ""} ${r.filial_nombre || ""} ${r.usuario_nombre || ""} ${r.horario_nombre || ""} ${r.fecha || ""}`.toLowerCase();
        if (!heno.includes(texto)) return false;
      }
      return true;
    });
    const porFecha = (a, b) => String(a.fecha).localeCompare(String(b.fecha));
    const porCreada = (a, b) => String(a.created_at || "").localeCompare(String(b.created_at || ""));
    return lista.sort({
      recientes:   (a, b) => porCreada(b, a),
      antiguas:    porCreada,
      proximas:    porFecha,
      lejanas:     (a, b) => porFecha(b, a),
    }[orden] || ((a, b) => porCreada(b, a)));
  }, [reservaciones, filiales, filtro, filterFilial, filterArea, filterDesde, filterHasta, busqueda, orden]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Si al filtrar la página actual se queda sin filas, volver a la primera.
  useEffect(() => {
    if (page > 1 && (page - 1) * PAGE_SIZE >= filtered.length) setPage(1);
  }, [filtered.length, page]);

  function limpiarTodo() {
    setFilterFilial(""); setFilterArea(""); setFilterDesde(""); setFilterHasta("");
    setBusqueda(""); setPage(1);
  }

  const FILTROS = [
    { key: "PENDIENTE", label: "Pendiente", color: "#f59e0b" },
    { key: "APROBADA",  label: "Aprobada",  color: "#10b981" },
    { key: "RECHAZADA", label: "Rechazada", color: "#ef4444" },
    // Acá caen también las que el sistema canceló solo al cumplirse el plazo
    // después de la fecha reservada.
    { key: "CANCELADA", label: "Cancelada", color: "#9ca3af" },
    { key: "TODAS",     label: t("portal.todas"), color: "#f97316" },
  ];

  // ── Piezas de una fila ──────────────────────────────────────────────────
  // Son funciones que devuelven elementos, NO componentes: declaradas adentro
  // de AdminReservaciones, React las vería como un tipo distinto en cada render
  // y remontaría la lista entera con cada tecla del buscador (el mismo motivo
  // por el que ModalOverlay vive a nivel de módulo). Llamarlas en vez de
  // montarlas evita eso sin tener que pasarles media docena de callbacks.
  function accionesDe(r) {
    return (
      <div className="pla-acciones">
        {r.estado === "PENDIENTE" && (
          <button onClick={() => abrirRevision(r)} style={{
            background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)",
            color: "#f97316", borderRadius: 9, padding: "8px 15px",
            cursor: "pointer", fontWeight: 700, fontSize: 12.5,
            display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
          }}>
            <i className="fa-solid fa-clipboard-check" /> {t("portal.revisar")}
          </button>
        )}
        {r.estado === "APROBADA" && (
          <button onClick={() => { setFechaModal(r); setNuevaFecha(r.fecha); }} style={{
            background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.3)",
            color: "#818cf8", borderRadius: 9, padding: "8px 15px",
            cursor: "pointer", fontWeight: 700, fontSize: 12.5,
            display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
          }}>
            {/* fa-calendar-pen no existe en el set Solid gratuito que usa el
                sistema: se renderizaba como un hueco en blanco. */}
            <i className="fa-solid fa-calendar-day" /> Cambiar fecha
          </button>
        )}
        <button onClick={() => setConfirmDel(r)} title="Eliminar reservación" style={{
          background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
          color: "#f87171", borderRadius: 9, padding: "8px 15px",
          cursor: "pointer", fontWeight: 700, fontSize: 12.5,
          display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        }}>
          <i className="fa-solid fa-trash" /> {t("common.eliminar")}
        </button>
      </div>
    );
  }

  /** Detalle que se abre con el chevron: todo lo que no cabe en la fila. */
  function detalleDe(r) {
    const campos = [
      ["Horario", `${r.horario_nombre}${r.horario_inicio ? ` (${String(r.horario_inicio).slice(0, 5)} - ${String(r.horario_fin).slice(0, 5)})` : ""}`],
      ["Solicitada el", r.created_at ? `${fechaLarga(r.created_at)} · ${desdeHace(r.created_at)}` : "-"],
      r.vence_el && r.estado === "PENDIENTE" ? ["Vence el", `${fechaLarga(r.vence_el)} - si nadie la revisa, se rechaza sola`] : null,
      r.revisado_por_nombre ? ["Revisada por", `${r.revisado_por_nombre}${r.revisado_at ? ` · ${fechaHoraLarga(r.revisado_at)}` : ""}`] : null,
      // Nadie la revisó: la cerró el barrido. Se dice quién fue para que no
      // quede como si un administrador la hubiera cancelado.
      r.cancelada_auto ? ["Cancelada por", `El sistema${r.revisado_at ? ` · ${fechaHoraLarga(r.revisado_at)}` : ""} - se cumplió el plazo después de la fecha reservada`] : null,
      // El rechazo por vencimiento tampoco tiene usuario, y a diferencia de la
      // cancelación no deja marca propia: se reconoce porque un rechazo hecho
      // a mano siempre guarda quién lo hizo.
      !r.revisado_por_nombre && !r.cancelada_auto && r.estado === "RECHAZADA" && r.revisado_at
        ? ["Rechazada por", `El sistema · ${fechaHoraLarga(r.revisado_at)} - venció el plazo para confirmar el pago`]
        : null,
      // Las que se resolvieron antes de que esto se mostrara no tienen nombre;
      // ahí va solo la fecha, en vez de un "Revisada por" vacío.
      !r.revisado_por_nombre && !r.cancelada_auto && r.estado === "APROBADA" && r.revisado_at
        ? ["Revisada el", fechaHoraLarga(r.revisado_at)]
        : null,
      r.fecha_pago ? ["Pago", `${fechaLarga(r.fecha_pago)}${r.num_doc_pago ? ` · doc. ${r.num_doc_pago}` : ""}`] : null,
    ].filter(Boolean);

    return (
      <div style={{
        borderTop: "1px dashed var(--border-card)", marginTop: 12, paddingTop: 12,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "8px 18px" }}>
          {campos.map(([k, v]) => (
            <div key={k}>
              <div style={colLabelSt}>{k}</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)", marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>

        {(r.factura_numero || r.recibo_numero || r.sin_costo) && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {r.sin_costo && (
              <span style={{
                background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.3)",
                color: "#60a5fa", borderRadius: 7, padding: "3px 9px", fontSize: 11, fontWeight: 700,
              }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }} /> Sin costo: no generó documentos
              </span>
            )}
            {r.factura_numero && (
              <span style={{
                background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)",
                color: "#f97316", borderRadius: 7, padding: "3px 9px", fontSize: 11, fontWeight: 700,
              }}>
                <i className="fa-solid fa-file-invoice" style={{ marginRight: 5 }} />{r.factura_numero}
              </span>
            )}
            {r.recibo_numero && (
              <span style={{
                background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)",
                color: "#10b981", borderRadius: 7, padding: "3px 9px", fontSize: 11, fontWeight: 700,
              }}>
                <i className="fa-solid fa-receipt" style={{ marginRight: 5 }} />{r.recibo_numero}
              </span>
            )}
          </div>
        )}

        {r.notas && (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)", fontStyle: "italic", lineHeight: 1.55 }}>
            <i className="fa-solid fa-quote-left" style={{ marginRight: 6, opacity: 0.5 }} />
            {r.notas}
          </p>
        )}
        {r.notas_admin && (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
            <i className="fa-solid fa-comment" style={{ marginRight: 6, color: "#f97316" }} />
            {r.notas_admin}
          </p>
        )}
        {r.comprobante_url && (
          <a href={r.comprobante_url} target="_blank" rel="noreferrer" style={{
            alignSelf: "flex-start", background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.25)", color: "#3b82f6", borderRadius: 8,
            padding: "7px 13px", fontWeight: 600, fontSize: 12, textDecoration: "none",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <i className="fa-solid fa-paperclip" /> Ver comprobante adjunto
          </a>
        )}
      </div>
    );
  }

  /** Tercera columna de la fila: lo que falta por hacer, o cuándo se pidió. */
  function columnaPlazoDe(r) {
    const hoy = new Date();
    const vence = r.estado === "PENDIENTE" && r.vence_el ? fechaLocal(r.vence_el) : null;
    if (vence) {
      const dias = diasEntre(hoy, vence);
      const urgente = dias <= 1;
      return (
        <div className="rsv-col-plazo" title={`Si nadie la revisa, se rechaza sola el ${fechaLarga(r.vence_el)}`}>
          <div style={{ ...colLabelSt, color: urgente ? "#f87171" : "var(--text-muted)" }}>
            <i className="fa-solid fa-hourglass-half" style={{ marginRight: 5 }} />Vence
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 3, color: urgente ? "#f87171" : "#f59e0b" }}>
            {dias < 0 ? "Vencida" : dias === 0 ? "Hoy" : dias === 1 ? "Mañana" : `En ${dias} días`}
          </div>
        </div>
      );
    }
    return (
      <div className="rsv-col-plazo">
        <div style={colLabelSt}>
          <i className="fa-regular fa-clock" style={{ marginRight: 5 }} />Creada hace
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-primary)", marginTop: 3 }}>{hace(r.created_at)}</div>
      </div>
    );
  }

  function filaDe(r, ultima) {
    const abierta = expandida === r.id;
    return (
      <div key={r.id} style={{
        padding: "14px 18px",
        borderBottom: ultima && !abierta ? "none" : "1px solid var(--border-card)",
        background: abierta ? "var(--hover-bg)" : "transparent",
        transition: "background .12s",
      }}>
        <div className="rsv-row">
          {/* Área + estado + filial/fecha */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#f97316", fontSize: 15,
            }}>
              <i className={`fa-solid ${iconoArea(r.area_nombre)}`} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 14.5, color: "var(--text-primary)" }}>{r.area_nombre}</span>
                <EstadoBadge estado={r.estado} auto={r.cancelada_auto} />
                {r.sin_costo && (
                  <span title="Aprobada sin cobro" style={{
                    background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.3)",
                    color: "#60a5fa", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 800,
                  }}>SIN COSTO</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                <span style={metaSt}><i className="fa-solid fa-house-chimney" style={{ fontSize: 10, opacity: 0.6 }} />{r.filial_nombre}</span>
                <span style={metaSt}><i className="fa-solid fa-calendar" style={{ fontSize: 10, opacity: 0.6 }} />{r.fecha}</span>
                {revisadoDe(r, true)}
              </div>
            </div>
          </div>

          {/* Horario + quién la pidió */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span style={metaSt}>
              <i className={`fa-solid ${iconoHorario(r.horario_nombre, r.horario_inicio)}`} style={{ fontSize: 11, color: "#f59e0b" }} />
              <span style={{ color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.horario_nombre}
              </span>
            </span>
            {r.usuario_nombre && (
              <span style={metaSt}>
                <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.usuario_nombre}</span>
              </span>
            )}
          </div>

          {/* Fecha de la reserva, en largo */}
          <div className="rsv-col-reserva">
            <div style={colLabelSt}><i className="fa-regular fa-calendar-check" style={{ marginRight: 5 }} />Reserva</div>
            <div style={{ fontSize: 12.5, color: "var(--text-primary)", marginTop: 3, whiteSpace: "nowrap" }}>{fechaLarga(r.fecha)}</div>
          </div>

          {columnaPlazoDe(r)}

          {/* Acciones + desplegar */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            {accionesDe(r)}
            <button onClick={() => setExpandida(abierta ? null : r.id)}
              title={abierta ? "Ocultar detalle" : "Ver detalle"} style={{
                width: 34, height: 34, flexShrink: 0, borderRadius: 9, cursor: "pointer",
                background: "transparent", border: "1px solid var(--border-card)",
                color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <i className={`fa-solid fa-chevron-${abierta ? "up" : "down"}`} style={{ fontSize: 11 }} />
            </button>
          </div>
        </div>

        {abierta && detalleDe(r)}
      </div>
    );
  }

  function tarjetaDe(r) {
    const abierta = expandida === r.id;
    return (
      <div key={r.id} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 13, padding: "15px 16px", display: "flex", flexDirection: "column", gap: 11,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "#f97316", fontSize: 14,
          }}>
            <i className={`fa-solid ${iconoArea(r.area_nombre)}`} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.area_nombre}
            </div>
            <div style={{ marginTop: 5 }}><EstadoBadge estado={r.estado} auto={r.cancelada_auto} /></div>
          </div>
          <button onClick={() => setExpandida(abierta ? null : r.id)} style={{
            width: 30, height: 30, flexShrink: 0, borderRadius: 8, cursor: "pointer",
            background: "transparent", border: "1px solid var(--border-card)",
            color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className={`fa-solid fa-chevron-${abierta ? "up" : "down"}`} style={{ fontSize: 10 }} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={metaSt}><i className="fa-solid fa-house-chimney" style={{ fontSize: 10, opacity: 0.6, width: 13 }} />{r.filial_nombre}</span>
          <span style={metaSt}>
            <i className="fa-regular fa-calendar-check" style={{ fontSize: 10, opacity: 0.6, width: 13 }} />
            {fechaLarga(r.fecha)}
          </span>
          <span style={metaSt}>
            <i className={`fa-solid ${iconoHorario(r.horario_nombre, r.horario_inicio)}`} style={{ fontSize: 11, color: "#f59e0b", width: 13 }} />
            {r.horario_nombre}
          </span>
          {r.usuario_nombre && (
            <span style={metaSt}>
              <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6, width: 13 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.usuario_nombre}</span>
            </span>
          )}
          {revisadoDe(r)}
        </div>

        <div style={{ borderTop: "1px solid var(--border-card)", paddingTop: 11 }}>
          {accionesDe(r)}
        </div>
        {abierta && detalleDe(r)}
      </div>
    );
  }

  return (
    <div className="portal-admin">
      {toastEl}
      <style>{`
        ${ESTILOS_LISTA}
        .rsv-row { display:grid; grid-template-columns:minmax(220px,1.7fr) minmax(130px,.9fr) 118px 118px auto;
                   gap:16px; align-items:center; }
        @media (max-width:1280px){
          .rsv-row { grid-template-columns:minmax(200px,1.6fr) minmax(120px,.9fr) 118px auto; }
          .rsv-col-reserva { display:none; }
        }
        @media (max-width:900px){
          .rsv-row { grid-template-columns:1fr; gap:12px; }
          .rsv-col-plazo { display:none; }
        }
      `}</style>

      <ConfirmModal
        open={!!confirmDel}
        name={confirmDel ? `${confirmDel.area_nombre} · ${confirmDel.filial_nombre} · ${confirmDel.fecha}` : ""}
        onConfirm={handleEliminar}
        onCancel={() => setConfirmDel(null)}
        loading={deleting}
      />

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Portal Admin /</span>
        </Link>
        <span className={styles.active}> {t("portal.reservaciones")}</span>
      </span>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <h2 className="mt-2 p-0 title">{t("portal.reservaciones")}</h2>
          <p style={{ margin: 0, color: "var(--text-muted)" }}>{t("portal.adminReservDesc")}</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setParamsOpen(true)} style={{
            background: "transparent", color: "var(--text-secondary)",
            border: "1px solid var(--border-card)", borderRadius: 9,
            padding: "10px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13,
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <i className="fa-solid fa-sliders" /> Parámetros
          </button>
          <button onClick={() => setNuevaOpen(true)} style={{
            background: "linear-gradient(135deg,#f97316,#fb923c)", color: "#fff", border: "none", borderRadius: 9,
            padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
            display: "flex", alignItems: "center", gap: 7,
            boxShadow: "0 4px 14px rgba(249,115,22,0.32)",
          }}>
            <i className="fa-solid fa-plus" /> Nueva reservación
          </button>
        </div>
      </div>

      {/* Resumen */}
      {!loading && reservaciones.length > 0 && (
        <div className="pla-stats">
          <StatCard icon="fa-calendar-days" label="Total de reservaciones" value={conteos.TODAS}
            total={conteos.TODAS} color="#f97316" mostrarPorcentaje={false} />
          <StatCard icon="fa-clock"        label="Pendientes" value={conteos.PENDIENTE} total={conteos.TODAS} color="#f59e0b" />
          <StatCard icon="fa-circle-check" label="Aprobadas"  value={conteos.APROBADA}  total={conteos.TODAS} color="#10b981" />
          <StatCard icon="fa-circle-xmark" label="Rechazadas" value={conteos.RECHAZADA} total={conteos.TODAS} color="#ef4444" />
        </div>
      )}

      <PestanasEstado
        opciones={FILTROS}
        valor={filtro}
        conteos={conteos}
        onChange={k => { setFiltro(k); setPage(1); setExpandida(null); }}
      />

      {/* Buscador + filtros + vista */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 14, overflow: "hidden",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border-card)" }}>
          <div className="pla-toolbar">
            <Buscador
              valor={busqueda}
              onChange={v => { setBusqueda(v); setPage(1); }}
              placeholder="Buscar por área, filial, residente u horario…"
            />

            <div className="pla-tools">
            <Desplegable icono="fa-calendar-days" activos={fechasActivas} ancho={380}
              etiqueta={fechasActivas ? `${filterDesde || "…"} → ${filterHasta || "…"}` : "Seleccione fechas"}>
              <RangoFechas
                titulo="Fecha de la reserva"
                desde={filterDesde} hasta={filterHasta}
                onDesde={v => { setFilterDesde(v); setPage(1); }}
                onHasta={v => { setFilterHasta(v); setPage(1); }}
                onLimpiar={() => { setFilterDesde(""); setFilterHasta(""); setPage(1); }}
              />
            </Desplegable>

            <Desplegable icono="fa-filter" etiqueta="Filtros" activos={filtrosActivos} ancho={300}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelSt}>Filial</label>
                  <SearchSelect
                    options={opcionesFilial}
                    value={filterFilial}
                    onChange={id => { setFilterFilial(id); setPage(1); }}
                    placeholder="Todas las filiales"
                    icon="fa-house-chimney"
                  />
                </div>
                <div>
                  <label style={labelSt}>Área común</label>
                  <SearchSelect
                    options={opcionesArea}
                    value={filterArea}
                    onChange={id => { setFilterArea(id); setPage(1); }}
                    placeholder="Todas las áreas"
                    icon="fa-location-dot"
                  />
                </div>
                {filtrosActivos > 0 && (
                  <button onClick={() => { setFilterFilial(""); setFilterArea(""); setPage(1); }} style={{
                    padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                    background: "transparent", border: "1px solid var(--border-card)",
                    color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
                  }}>
                    Quitar filtros
                  </button>
                )}
              </div>
            </Desplegable>

            <SelectorVista vista={vista} onChange={setVista} />
            </div>
          </div>
        </div>

        {/* "reservación" lleva tilde; "reservaciones" no. Pegarle "es" al
            singular daba "reservaciónes". */}
        <BarraResultados
          texto={`${filtered.length} ${filtered.length === 1 ? "reservación" : "reservaciones"}`}
          hayFiltros={filtrosActivos > 0 || fechasActivas > 0 || !!busqueda}
          onLimpiar={limpiarTodo}
          orden={orden}
          onOrden={setOrden}
          opcionesOrden={OPCIONES_ORDEN}
        />

        {/* Listado */}
        {loading ? (
          <Cargando texto="Cargando reservaciones…" />
        ) : filtered.length === 0 ? (
          <Vacio
            icono="fa-calendar-xmark"
            titulo={reservaciones.length === 0 ? t("portal.sinReservaciones") : "Sin resultados"}
            detalle={reservaciones.length === 0
              ? "No hay reservaciones aún."
              : "Probá cambiando los filtros o la búsqueda."}
          />
        ) : vista === "tarjetas" ? (
          <>
            <div className="pla-grid">
              {paged.map(r => tarjetaDe(r))}
            </div>
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        ) : (
          <>
            {paged.map((r, i) => filaDe(r, i === paged.length - 1))}
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        )}
      </div>

      {/* ── Nueva reservación (a nombre de una filial) ── */}
      {nuevaOpen && (
        <NuevaReservacionModal
          filiales={filiales}
          onClose={() => setNuevaOpen(false)}
          onSaved={(r) => {
            setNuevaOpen(false);
            showToast(r.estado === "APROBADA" ? "Reservación registrada y aprobada" : "Reservación registrada", true);
            setFiltro(r.estado === "APROBADA" ? "APROBADA" : "PENDIENTE");
            setPage(1);
            load();
          }}
        />
      )}

      {paramsOpen && (
        <ParametrosReservacionModal
          onClose={() => setParamsOpen(false)}
          onSaved={() => showToast("Parámetros guardados", true)}
        />
      )}

      {/* ── Revisar: aprobar (con datos del pago) o rechazar ── */}
      {modal && (
        <ModalOverlay width={640} onClose={() => !saving && setModal(null)}>
          <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
                {t("portal.revisarReserva")}
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                {modal.area_nombre} · {modal.filial_nombre} · {modal.fecha} · {modal.horario_nombre}
              </p>
            </div>
            <button onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            {(modal.notas || modal.comprobante_url) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {modal.notas && (
                  <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>
                    <i className="fa-solid fa-quote-left" style={{ marginRight: 6, opacity: 0.5 }} />
                    {modal.notas}
                  </p>
                )}
                {modal.comprobante_url && (
                  <a href={modal.comprobante_url} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
                    background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)",
                    color: "#3b82f6", borderRadius: 8, padding: "8px 14px",
                    fontSize: 13, fontWeight: 600, textDecoration: "none",
                  }}>
                    <i className="fa-solid fa-file" /> Ver comprobante adjunto
                  </a>
                )}
              </div>
            )}
            <div>
              <label style={labelSt}>{t("portal.notasAdmin")} (opcional)</label>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                placeholder={t("portal.motivoPlaceholder")}
                style={fldStyle({ resize: "vertical" })}
              />
            </div>

            <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, padding: "14px 16px" }}>
              <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                <i className="fa-solid fa-receipt" style={{ marginRight: 6, color: "#f97316" }} />
                Datos del pago
              </p>
              <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-secondary)" }}>
                {pago.metodo_pago === SIN_COSTO
                  ? "Esta reservación se aprueba sin cobrar: no se genera ningún documento."
                  : "Solo se usan al aprobar: generan la factura y el recibo de la filial en esta cuenta bancaria."}
              </p>
              <DatosPagoFields
                pago={pago}
                onChange={p => { setPago(prev => ({ ...prev, ...p })); setRevErr(""); }}
                area={{ precio_colones: modal.area_precio_colones, precio_dolares: modal.area_precio_dolares }}
              />
            </div>

            {revErr && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171", borderRadius: 8,
                padding: "10px 14px", color: "#f87171", fontSize: 13,
              }}>
                {revErr}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button onClick={handleRechazar} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "1px solid rgba(248,113,113,0.4)",
                background: "rgba(248,113,113,0.1)", color: "#f87171",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <i className="fa-solid fa-xmark" /> {t("portal.rechazar")}
              </button>
              <button onClick={handleAprobar} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "none",
                background: "#22c55e", color: "#fff",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Procesando…</>
                  : <><i className="fa-solid fa-check" /> {pago.metodo_pago === SIN_COSTO ? "Aprobar sin cobro" : t("portal.aprobar")}</>}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Date change modal ── */}
      {fechaModal && (
        <ModalOverlay onClose={() => setFechaModal(null)}>
          <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between" }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>Cambiar fecha</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                {fechaModal.area_nombre} · {fechaModal.filial_nombre}
              </p>
            </div>
            <button onClick={() => setFechaModal(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <label style={labelSt}>Nueva fecha</label>
            <input type="date" value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
              style={{ ...fldStyle(), marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setFechaModal(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-card)",
                background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleCambiarFecha} disabled={savingFecha} style={{
                padding: "9px 22px", borderRadius: 8, border: "none",
                background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: savingFecha ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {savingFecha
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                  : <><i className="fa-solid fa-floppy-disk" /> {t("common.guardar")}</>}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
